import { FaMouse } from "react-icons/fa";
import { LuRotate3D } from "react-icons/lu";
import { LuScale3D } from "react-icons/lu";
import { FaCircleDot } from "react-icons/fa6";
import {
  TbCone,
  TbCube,
  TbCylinder,
  TbPyramid,
  TbSphere,
} from "react-icons/tb";
import { Input, Select } from "semantic-ui-react";
import { FaArrowsUpDownLeftRight } from "react-icons/fa6";
import { FiCheck, FiX } from "react-icons/fi";
import { BsTrash3 } from "react-icons/bs";
import NumberInput from "../general/NumberInput";
import ParameterInput from "../general/ParameterInput";
import ActionButton from "../general/ActionButton";
import { colors } from "../../utils/colors";

export default function ControlPanel({
  onPlace,
  onCancel,
  onUpdateObject,
  onDeleteObject,
  transformMode,
  onTransformModeChange,
  transformAxis,
  onTransformAxisChange,
  isPlaced,
  selectedShape,
  onShapeChange,
  position,
  visualPosition,
  onPositionInputChange,
  shapeParams,
  onShapeParamsChange,
  editingObjectId,
}) {
  const editingPosition = position;

  const minValue = 0;
  const maxValue = 10;

  const shapeConfig = {
    sphere: {
      icon: <TbSphere fill={colors.sphere()} stroke={colors.sphere()} />,
      name: "Sphere",
      color: colors.sphere(),
      params: [{ label: "Radius", step: 0.1, defaultValue: 0.5 }],
    },
    cube: {
      icon: <TbCube fill={colors.cube()} stroke={colors.cube()} />,
      name: "Cube",
      color: colors.cube(),
      params: [
        { label: "Width", step: 0.1, defaultValue: 2 },
        { label: "Height", step: 0.1, defaultValue: 8 },
        { label: "Depth", step: 0.1, defaultValue: 4 },
      ],
    },
    cylinder: {
      icon: <TbCylinder fill={colors.cylinder()} stroke={colors.cylinder()} />,
      name: "Cylinder",
      color: colors.cylinder(),
      params: [
        { label: "Radius", step: 0.1, defaultValue: 1 },
        { label: "Height", step: 0.1, defaultValue: 4 },
      ],
    },
    cone: {
      icon: <TbCone fill={colors.cone()} stroke={colors.cone()} />,
      name: "Cone",
      color: colors.cone(),
      params: [
        { label: "Radius", step: 0.1, defaultValue: 1 },
        { label: "Height", step: 0.1, defaultValue: 4 },
      ],
    },
    pyramid: {
      icon: <TbPyramid fill={colors.pyramid()} stroke={colors.pyramid()} />,
      name: "Pyramid",
      color: colors.pyramid(),
      params: [
        { label: "Radius", step: 0.1, defaultValue: 1 },
        { label: "Height", step: 0.1, defaultValue: 4 },
      ],
    },
    torus: {
      icon: <FaCircleDot fill={colors.torus()} stroke={colors.torus()} />,
      name: "Torus",
      color: colors.torus(),
      params: [
        { label: "Radius", step: 0.1, defaultValue: 4 },
        { label: "Thickness", step: 0.1, defaultValue: 1 },
      ],
    },
  };

  const shapeOptions = Object.entries(shapeConfig).map(([key, config]) => ({
    key,
    text: (
      <span className="icon-text">
        {config.icon} {config.name}
      </span>
    ),
    value: key,
  }));

  const transformTools = [
    {
      mode: "drag",
      icon: <FaArrowsUpDownLeftRight size={16} />,
      label: "Drag",
    },
    { mode: "rotate", icon: <LuRotate3D size={16} />, label: "Rotate" },
    {
      mode: "scale",
      icon: <LuScale3D size={16} />,
      label: "Scale",
    },
  ];

  const rotationAxes = [
    { axis: "xyz", label: "XYZ", color: colors.info() },
    {
      axis: "mouse",
      label: <FaMouse size={16} />,
      color: colors.info(),
    },
  ];

  return (
    <div className="control-panel">
      <div className="control-section">
        <span className="control-label">Shape:</span>

        <Select
          compact
          value={selectedShape}
          onChange={(e, { value }) => onShapeChange(value)}
          options={shapeOptions}
          style={{
            minWidth: "140px",
            borderRadius: "10px",
          }}
          className="neutral"
          upward
        />
      </div>

      <div className="flex flex-column gap-2">
        <span className="control-label" style={{ marginBottom: "4px" }}>
          Parameters:
        </span>

        {shapeConfig[selectedShape]?.params.map((param, index) => (
          <ParameterInput
            key={`${selectedShape}-${index}`}
            label={param.label}
            value={shapeParams?.[index] ?? param.defaultValue}
            min={minValue}
            max={maxValue}
            step={param.step}
            onChange={onShapeParamsChange}
            index={index}
          />
        ))}
      </div>

      <div className="control-section">
        <span className="control-label">Tool:</span>

        {transformTools.map(({ mode, icon, label }) => (
          <div
            key={mode}
            className={
              transformMode === mode ? "chip chip-active" : "chip chip-inactive"
            }
            onClick={() => onTransformModeChange(mode)}
          >
            {icon}
            <span>{label}</span>
          </div>
        ))}
      </div>

      {transformMode === "rotate" && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "12px",
            justifyContent: "center",
          }}
        >
          <span
            style={{
              fontSize: "14px",
              fontWeight: "600",
              color: "#374151",
              marginRight: "8px",
            }}
          >
            Rotation:
          </span>

          {rotationAxes.map(({ axis, label, color }) => (
            <div
              key={axis}
              className="axis-selector"
              style={{
                backgroundColor:
                  transformAxis === axis ? color : colors.white(),
                color:
                  transformAxis === axis ? colors.white() : colors.gray500(),
                borderColor: transformAxis === axis ? color : colors.gray200(),
              }}
              onClick={() => onTransformAxisChange(axis)}
            >
              {label}
            </div>
          ))}
        </div>
      )}

      {transformMode === "drag" && (
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <span
            style={{
              fontSize: "14px",
              fontWeight: "600",
              color: "#374151",
              marginRight: "8px",
            }}
          >
            Position:
          </span>

          <div style={{ display: "flex", gap: "4px", alignItems: "center" }}>
            <label
              style={{
                fontSize: "12px",
                fontWeight: "600",
                color: colors.error(),
              }}
            >
              X:
            </label>
            <NumberInput
              placeholder="0"
              step="0.1"
              onChange={(e) => {
                onPositionInputChange?.(0, e.target.value);
              }}
              value={parseFloat(visualPosition[0].toFixed(1))}
              style={{
                width: "80px",
                height: "32px",
              }}
            />
          </div>

          <div style={{ display: "flex", gap: "4px", alignItems: "center" }}>
            <label
              style={{
                fontSize: "12px",
                fontWeight: "600",
                color: colors.info(),
              }}
            >
              Y:
            </label>
            <NumberInput
              type="number"
              step="0.1"
              onChange={(e) => {
                onPositionInputChange?.(1, e.target.value);
              }}
              value={parseFloat(visualPosition[1].toFixed(1))}
              style={{
                width: "90px",
                height: "32px",
              }}
            />
          </div>

          <div style={{ display: "flex", gap: "4px", alignItems: "center" }}>
            <label
              style={{
                fontSize: "12px",
                fontWeight: "600",
                color: colors.success(),
              }}
            >
              Z:
            </label>
            <Input
              type="number"
              step="0.1"
              onChange={(e) => {
                onPositionInputChange?.(2, e.target.value);
              }}
              value={parseFloat(visualPosition[2].toFixed(1))}
              style={{
                width: "90px",
                height: "32px",
              }}
            />
          </div>
        </div>
      )}

      <div style={{ display: "flex", gap: "12px", justifyContent: "center" }}>
        {!isPlaced ? (
          <>
            <ActionButton
              onClick={onPlace}
              variant="success"
              icon={<FiCheck size={16} />}
            >
              Place
            </ActionButton>

            <ActionButton
              onClick={onCancel}
              variant="error"
              icon={<FiX size={16} />}
            >
              Cancel
            </ActionButton>
          </>
        ) : editingObjectId ? (
          <>
            <ActionButton
              onClick={() => onUpdateObject?.(editingObjectId)}
              variant="info"
              icon={<FiCheck size={16} />}
            >
              Update
            </ActionButton>

            <ActionButton
              onClick={() => onDeleteObject?.(editingObjectId)}
              variant="error"
              icon={<BsTrash3 color={colors.white()} size={18} />}
            >
              Delete
            </ActionButton>

            <ActionButton
              onClick={onCancel}
              variant="neutral"
              icon={<FiX size={16} />}
            >
              Cancel
            </ActionButton>
          </>
        ) : null}
      </div>
    </div>
  );
}


